"use client";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faSpinnerThird } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type VariantProps, cva } from "class-variance-authority";
import { type JSX, forwardRef } from "react";
import { cn } from "../../..";
import { type AnchorVariantProps, anchor } from "./Anchor";
import { type IconProps, icon } from "./Icon";

export const button = cva(
	"no-prose appearance-none inline-block font-primary font-bold text-center border-2 rounded motion-safe:transition disabled:opacity-70 disabled:cursor-not-allowed focus:outline-none",
	{
		variants: {
			variant: {
				contained: "",
				outlined: "bg-transparent",
			},
			color: {
				primary: "border-primary enter:border-primary-medium",
				"primary-medium": "border-primary-medium enter:border-primary-dark",
				secondary: "border-secondary enter:border-secondary-medium",
				dark: "border-dark enter:border-black",
				white: "border-white enter:border-shades",
				success: "border-success enter:border-success-medium",
				warning: "border-warning enter:border-warning-medium",
				danger: "border-danger enter:border-danger-medium",
			},
			size: {
				small: "text-xs py-1 px-2",
				medium: "text-sm py-2 px-4",
				large: "text-base py-3 px-6",
			},
		},
		compoundVariants: [
			{
				variant: "contained",
				color: "primary",
				class: "text-white bg-primary enter:bg-primary-medium",
			},
			{
				variant: "contained",
				color: "primary-medium",
				class: "text-white bg-primary-medium enter:bg-primary-dark",
			},
			{
				variant: "contained",
				color: "secondary",
				class: "text-white bg-secondary enter:bg-secondary-medium",
			},
			{
				variant: "contained",
				color: "dark",
				class: "text-white bg-dark enter:bg-black",
			},
			{
				variant: "contained",
				color: "white",
				class: "text-dark bg-white enter:bg-shades",
			},
			{
				variant: "contained",
				color: "success",
				class: "text-white bg-success enter:bg-success-medium",
			},
			{
				variant: "contained",
				color: "warning",
				class: "text-white bg-warning enter:bg-warning-medium",
			},
			{
				variant: "contained",
				color: "danger",
				class: "text-white bg-danger enter:bg-danger-medium",
			},
			{
				variant: "outlined",
				color: "primary",
				class: ["text-primary", "enter:text-primary-medium", "enter:bg-primary-light/50"],
			},
			{
				variant: "outlined",
				color: "primary-medium",
				class: ["text-primary-medium", "enter:text-primary-dark", "enter:bg-primary-light/50"],
			},
			{
				variant: "outlined",
				color: "secondary",
				class: ["text-secondary", "enter:text-secondary-medium", "enter:bg-secondary-light/50"],
			},
			{
				variant: "outlined",
				color: "dark",
				class: ["text-dark", "enter:text-black", "enter:bg-shades-medium-light/50"],
			},
			{
				variant: "outlined",
				color: "white",
				class: ["text-white", "enter:text-white", "enter:bg-shades-light/50"],
			},
			{
				variant: "outlined",
				color: "success",
				class: ["text-success", "enter:text-success-medium", "enter:bg-success-light/50"],
			},
			{
				variant: "outlined",
				color: "warning",
				class: ["text-warning", "enter:text-warning-medium", "enter:bg-warning-light/50"],
			},
			{
				variant: "outlined",
				color: "danger",
				class: ["text-danger", "enter:text-danger-medium", "enter:bg-danger-light/50"],
			},
		],

		defaultVariants: {
			variant: "contained",
			color: "primary-medium",
			size: "medium",
		},
	},
);

export type ButtonVariantProps = VariantProps<typeof button>;

type TypeProps =
	| {
			variant: "link";
			color?: AnchorVariantProps["color"];
			size?: never;
	  }
	| {
			variant?: ButtonVariantProps["variant"];
			color?: ButtonVariantProps["color"];
			size?: ButtonVariantProps["size"];
	  };

interface BaseProps {
	isLoading?: boolean;
}

export type ButtonProps = BaseProps & IconProps & TypeProps & JSX.IntrinsicElements["button"];

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function ButtonRef(props, ref) {
	const {
		variant,
		color,
		size,
		starticon,
		endicon,
		iconColor,
		iconSize,
		isLoading,
		className,
		children,
		...remainingProps
	} = props;

	return (
		<button
			disabled={isLoading || props.disabled}
			className={cn(
				variant === "link"
					? anchor({ color: color as AnchorVariantProps["color"] })
					: button({
							color: color as ButtonVariantProps["color"],
							size,
							variant,
						}),
				{
					"cursor-pointer": !(isLoading || props.disabled),
					"cursor-not-allowed opacity-70": isLoading || props.disabled,
				},
				className,
			)}
			type={remainingProps.type ?? "button"}
			{...remainingProps}
			ref={ref}
		>
			{(starticon || (!(starticon || endicon) && isLoading)) && (
				<FontAwesomeIcon
					className={cn(icon({ color: iconColor }), {
						"motion-safe:animate-spin": isLoading,
						"mr-2": children,
					})}
					icon={isLoading ? faSpinnerThird : (starticon as IconProp)}
					size={iconSize === "default" ? undefined : (iconSize ?? "sm")}
					fixedWidth
				/>
			)}

			{children}

			{endicon && (
				<FontAwesomeIcon
					className={cn(icon({ color: iconColor }), {
						"motion-safe:animate-spin": isLoading && !starticon,
						"ml-2": children,
					})}
					icon={isLoading && !starticon ? faSpinnerThird : endicon}
					size={iconSize === "default" ? undefined : (iconSize ?? "sm")}
					fixedWidth
				/>
			)}
		</button>
	);
});
